import React, { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { AppDispatch } from 'src/redux/store';
import { useDataVault } from 'src/contexts/DataVaultContext';
import useAuth from 'src/hooks/useAuth';
import { RENTER_PATHS } from 'src/routes/renterPaths';
import PageHeader from 'src/components/NewRenterInterface/PageHeader/PageHeader';
import MemberCarousel from 'src/components/NewRenterInterface/Home/MemberCarousel';
import type { ManagedMember } from 'src/components/NewRenterInterface/Home/MemberCarousel';
import OnBehalfOfBanner from 'src/components/NewRenterInterface/shared/OnBehalfOfBanner';
import {
  fetchProfileSections,
  selectSections,
} from 'src/redux/slices/DataVault/dataVaultSectionsSlice';
import {
  fetchHistoryCertifications,
  selectHistoryCertifications,
  scopeKey as historyScopeKey,
} from 'src/redux/slices/DataVault/dataVaultHistoryCertificationsSlice';
import {
  fetchManagedMembers,
  selectManagedMembers,
} from 'src/redux/slices/DataVault/dataVaultManagedMembersSlice';
import {
  fetchPersonalInfo,
  selectPersonalInfo,
  scopeKey,
} from 'src/redux/slices/DataVault/dataVaultPersonalInfoSlice';

import { PersonalInfoCard } from './components/PersonalInfoCard';
import { PersonalInfoCardSkeleton } from './components/PersonalInfoCardSkeleton';
import { interviewActions } from 'src/redux/slices/Interview/interviewSlice';
import { PQCard } from './components/PQCard';
import { PQCardSkeleton } from './components/PQCardSkeleton';
import { HistoryPreview } from './components/HistoryPreview';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function mapDvMembersToCarousel(
  members: {
    id: number;
    first_name: string;
    last_name: string;
    initials: string;
    is_hoh: boolean;
    relationship_label: string;
    is_self: boolean;
  }[]
): ManagedMember[] {
  return members.map((m) => ({
    userId: m.id,
    firstName: m.first_name,
    lastName: m.last_name,
    displayName: `${m.first_name} ${m.last_name}`,
    initials: m.initials,
  }));
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DataVaultLandingPage(): React.ReactElement {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const theme = useTheme();
  const { targetUserId, actingOnMemberId } = useDataVault();
  const { user } = useAuth();

  // -------------------------------------------------------------------------
  // Selectors
  // -------------------------------------------------------------------------

  const sectionsEntry = useSelector(selectSections(targetUserId));
  const historyEntry = useSelector(
    selectHistoryCertifications(historyScopeKey(actingOnMemberId))
  );
  const managedMembersState = useSelector(selectManagedMembers);
  const personalInfoEntry = useSelector(
    selectPersonalInfo(scopeKey(actingOnMemberId))
  );

  // -------------------------------------------------------------------------
  // Data fetching
  // -------------------------------------------------------------------------

  useEffect(() => {
    if (
      managedMembersState.status !== 'loading' &&
      managedMembersState.status !== 'succeeded' &&
      managedMembersState.status !== 'failed'
    ) {
      dispatch(fetchManagedMembers({}));
    }
  }, [dispatch, managedMembersState.status]);

  useEffect(() => {
    dispatch(fetchPersonalInfo({ memberId: actingOnMemberId }));
  }, [dispatch, actingOnMemberId]);

  useEffect(() => {
    if (sectionsEntry?.status !== 'loading') {
      dispatch(
        fetchProfileSections({
          targetUserId,
          actingOnMemberId,
          forceRefresh: true,
        })
      );
    }
  }, [dispatch, targetUserId, actingOnMemberId]);

  useEffect(() => {
    if (
      historyEntry?.status !== 'loading' &&
      historyEntry?.status !== 'succeeded' &&
      historyEntry?.status !== 'failed'
    ) {
      dispatch(fetchHistoryCertifications({ actingOnMemberId }));
    }
  }, [dispatch, actingOnMemberId, historyEntry?.status]);

  // -------------------------------------------------------------------------
  // Derived state
  // -------------------------------------------------------------------------

  const sectionsLoading = !sectionsEntry || sectionsEntry.status === 'loading';
  const sectionsFailed = sectionsEntry?.status === 'failed';

  const sections = sectionsEntry?.sections ?? [];
  const completedSections = sections.filter(
    (s) => s.status === 'complete'
  ).length;
  const totalSections = sections.length;
  const allComplete =
    totalSections > 0 && sections.every((s) => s.status === 'complete');

  const interviewPath = actingOnMemberId
    ? `/renter/data-vault/members/${actingOnMemberId}/pq/answer`
    : '/renter/data-vault/pq/answer';

  const pqPath = actingOnMemberId
    ? `/renter/data-vault/members/${actingOnMemberId}/pq`
    : '/renter/data-vault/pq';

  const personalInfoEditUserId = actingOnMemberId ?? user?.id;
  const personalInfoEditUrl = personalInfoEditUserId
    ? `/${RENTER_PATHS.HOME}/${RENTER_PATHS.ACCOUNT_HUB}/${personalInfoEditUserId}/${RENTER_PATHS.ACCOUNT_HUB_PERSONAL_INFO}`
    : null;

  // Pure helpers (helpers with no surveys) are filtered server-side by the
  // managed_members endpoint, so we use the list as-is. Helpers who are also
  // subjects elsewhere are intentionally retained by the server.
  const members = managedMembersState.members;

  const showMemberCarousel = members.length > 1;

  const carouselMembers = mapDvMembersToCarousel(members);
  const selfMember = members.find((m) => m.is_self);
  const selectedUserId = actingOnMemberId ?? selfMember?.id ?? targetUserId;

  const subjectMember = actingOnMemberId
    ? members.find((m) => m.id === actingOnMemberId)
    : null;
  const subjectName = subjectMember
    ? `${subjectMember.first_name} ${subjectMember.last_name}`.trim()
    : null;

  const handleCarouselSelect = (userId: number): void => {
    const member = members.find((m) => m.id === userId);
    if (!member) {
      return;
    }
    if (member.is_self) {
      navigate('/renter/data-vault');
    } else {
      navigate(`/renter/data-vault/members/${userId}`);
    }
  };

  // -------------------------------------------------------------------------
  // Render — page always renders, each section handles its own loading state
  // -------------------------------------------------------------------------

  return (
    <Box>
      <PageHeader
        title={subjectMember ? `${subjectMember.first_name}'s Data` : 'My Data'}
        subtitle={
          subjectMember
            ? `${subjectMember.first_name}'s personal information hub`
            : 'Your personal information hub'
        }
      />

      <Box
        sx={{
          backgroundColor: theme.palette.background.default,
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          px: '16px',
          pb: '100px',
        }}
      >
        {/* Member carousel — renders immediately, no loading state needed */}
        {showMemberCarousel && (
          <Box sx={{ mt: -1 }}>
            <MemberCarousel
              members={carouselMembers}
              selectedUserId={selectedUserId}
              onSelect={handleCarouselSelect}
            />
          </Box>
        )}

        {/* On-behalf-of banner — shown when acting on behalf of another member */}
        <OnBehalfOfBanner name={subjectName} variant="card" />

        {/* Personal info card */}
        {personalInfoEntry.status === 'loading' ||
        personalInfoEntry.status === 'idle' ||
        !personalInfoEntry.data ? (
          <PersonalInfoCardSkeleton />
        ) : (
          <PersonalInfoCard
            completed={personalInfoEntry.data.completed}
            total={personalInfoEntry.data.total}
            editUrl={personalInfoEditUrl}
          />
        )}

        {/* Pronto Questionnaire card */}
        {sectionsLoading ? (
          <PQCardSkeleton />
        ) : sectionsFailed ? (
          <Box
            sx={{
              bgcolor: 'background.paper',
              borderRadius: '16px',
              boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1.5,
              py: 3,
              px: 2,
            }}
          >
            <Typography
              role="alert"
              sx={{
                color: 'text.secondary',
                fontSize: 14,
                textAlign: 'center',
              }}
            >
              Failed to load questionnaire.
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() =>
                dispatch(
                  fetchProfileSections({
                    targetUserId,
                    actingOnMemberId,
                    forceRefresh: true,
                  })
                )
              }
            >
              Retry
            </Button>
          </Box>
        ) : (
          <PQCard
            completedSections={completedSections}
            totalSections={totalSections}
            allComplete={allComplete}
            pqPath={interviewPath}
            vaultSummaryPath={pqPath}
            onContinueClick={() =>
              dispatch(interviewActions.clearAllDeferred())
            }
          />
        )}

        {/* History preview */}
        <HistoryPreview
          certifications={historyEntry?.certifications ?? []}
          actingOnMemberId={actingOnMemberId}
          isLoading={!historyEntry || historyEntry.status === 'loading'}
        />
      </Box>
    </Box>
  );
}
